import React from 'react';
import { Autocomplete, Box, Chip, FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { THEME_COLOR, inputLabelFocusedStyle, selectFocusedStyle } from '../../../theme/formStyles';
import { PROJECT_TYPES, userLabel } from './editorOptions';

const fieldSx = { '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME_COLOR }, '& .MuiInputLabel-root.Mui-focused': { color: THEME_COLOR } };

// Project details: the same fields as the Settings form (facility, budget, owners, description) plus
// type of project and contract length. A new project picks the client first, then one of its facilities.
export default function ProjectDetailsSection({ project, isNew, clients, facilities, greenUsers, errors, onChange, onClientChange }) {
    return (
        <Box sx={{ border: '1px solid #e5e9ee', borderRadius: 3, p: 2, bgcolor: '#fff' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '14.5px', mb: 1.5 }}>Project details</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' }, gap: 1.5 }}>
                {isNew && (
                    <Autocomplete
                        size="small"
                        options={clients}
                        getOptionLabel={(c) => c?.name || ''}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        value={clients.find(c => c.id === project.client_id) || null}
                        onChange={(e, c) => onClientChange(c ? c.id : '')}
                        renderInput={(params) => <TextField {...params} label="Client *" sx={fieldSx} />}
                    />
                )}
                <FormControl size="small" error={!!errors.facility} disabled={isNew && !project.client_id}>
                    <InputLabel sx={inputLabelFocusedStyle}>Facility Name *</InputLabel>
                    <Select label="Facility Name *" value={facilities.some(f => f.id === project.facility_id) ? project.facility_id : ''}
                        onChange={(e) => onChange('facility_id', e.target.value)} sx={selectFocusedStyle} MenuProps={{ disableScrollLock: true }}>
                        {facilities.map(f => <MenuItem key={f.id} value={f.id}>{f.facility_name}</MenuItem>)}
                    </Select>
                    {errors.facility && <FormHelperText>{errors.facility}</FormHelperText>}
                    {isNew && !project.client_id && <FormHelperText>Pick the client first</FormHelperText>}
                </FormControl>
                <FormControl size="small">
                    <InputLabel sx={inputLabelFocusedStyle}>Type of project</InputLabel>
                    <Select label="Type of project" value={project.project_type || ''} onChange={(e) => onChange('project_type', e.target.value)} sx={selectFocusedStyle} MenuProps={{ disableScrollLock: true }}>
                        <MenuItem value=""><em>Not set</em></MenuItem>
                        {PROJECT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small">
                    <InputLabel sx={inputLabelFocusedStyle}>Contract length</InputLabel>
                    <Select label="Contract length" value={project.contract_years ?? ''} onChange={(e) => onChange('contract_years', e.target.value === '' ? null : Number(e.target.value))} sx={selectFocusedStyle} MenuProps={{ disableScrollLock: true }}>
                        <MenuItem value=""><em>Not set</em></MenuItem>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(y => <MenuItem key={y} value={y}>{y} year{y > 1 ? 's' : ''} from handover</MenuItem>)}
                    </Select>
                </FormControl>
                <TextField size="small" type="number" label="Project Budget" value={project.project_budget ?? ''} onChange={(e) => onChange('project_budget', e.target.value)} sx={fieldSx} />
                <Autocomplete
                    multiple size="small"
                    options={greenUsers}
                    getOptionLabel={userLabel}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={project.project_owner || []}
                    onChange={(e, val) => onChange('project_owner', val)}
                    renderTags={(value, getTagProps) => value.map((o, i) => (
                        <Chip key={i} label={userLabel(o)} size="small" {...getTagProps({ index: i })}
                            sx={{ backgroundColor: THEME_COLOR, color: '#fff', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' } }} />
                    ))}
                    renderInput={(params) => <TextField {...params} label="Project Owner / SPOC" sx={fieldSx} />}
                    sx={{ gridColumn: { md: isNew ? 'span 2' : 'span 1' } }}
                />
                <TextField size="small" label="Description of the Project" multiline minRows={1} value={project.description}
                    onChange={(e) => onChange('description', e.target.value)} sx={{ ...fieldSx, gridColumn: { md: '1 / -1' } }} />
            </Box>
        </Box>
    );
}
